import _ from "underscore";
import rison from "rison";
import DriveView from "../../DriveView";
import { htmlEscape, show, hide, empty } from "../../utils/vanilla";
import { Trailer, ITrailer } from "../../model/Trailer";
import { generateChecklist, ListView } from "../framework/checklist";
import ManageTrailersView from "../assets/manageTrailers";
import { handleApi } from "../../utils/error";
import { BaseCache, makeTrailerCache } from "../../utils/storage/cache";
import * as logger from "../../utils/logger";
import { Dialog } from "../framework/dialog";
import constants from "../../constants";
import { getCurrentVehicle } from "../../utils/device";
import Api from "../../services/api";
import Sync from "../../sync/sync";
import { CancelablePromise as Promise } from "../../utils/cancelablePromise";
import { getServerDate } from "../../utils/storage/date";
import idSerializer from "geotab-uuid";
import Spinner, { BigSpinner } from "../../utils/spinner";
import { SyncType } from "../../sync/sync.types";
import { PageLevel } from "../../store/mobile/types";
import DetachAllPanelView from "./detachAllHelper";
import { getAttachmentsFromState, setAttachmentsInState } from "./helper";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";

import pageHTML from "./selectTrailer.tmpl";
import toastr from "../../utils/toastr";
import { TMultiCallParam } from "../../utils/api.interfaces";
import PerformanceProfilerFactory from "../../utils/performanceProfiler/performanceProfilerFactory";
import { MeasureName } from "../../utils/performanceProfiler/measureName";
import {
    cleanTrailerName,
    hasTrailerWithSameName,
    mapDeviceToTrailer,
    runMultiCallSearch,
    createTrailerAttachment,
    addNewTrailer,
    createAndCacheOfflineTrailer,
    detachAllTrailers
} from "../../modules/assets/helpers/trailersHelper";

const MAX_SEARCH_ASSETS = 20;
const TRAILER_ATTACHMENT_ERROR_MESSAGE = "Trailer attachment error";

class StaleSearchError extends Error {
    constructor() { super("Stale search discarded"); }
}

interface IActiveSearch {
    key: string;
    promise: Promise<void>;
    token: number;
}

export default class SelectTrailerView extends DriveView<Backbone.Model> {

    protected strings = [
        "Searching for trailers...",
        "Searching for '{0}'",
        "Could not find any trailers matching your search terms.",
        "Please try again.",
        "Saving in progress",
        "Add",
        "Cannot attach more than {0} trailers. Please detach existing trailers in order to add new assets.",
        "Are you sure you want to detach {0} trailers?",
        "You must select a vehicle to attach a trailer.",
        "Could not find trailers due to connectivity issues.",
        "A trailer named {0} is already attached"
    ];
    private readonly template = _.template(pageHTML);
    private readonly api: Api;
    private readonly trailerCache: BaseCache<ITrailer>;
    private readonly sync: Sync;
    private readonly user: DeepReadonly<IUser> | null;
    private readonly workflow: boolean;
    private readonly manageTrailersView: ManageTrailersView;
    private readonly detachAllView: DetachAllPanelView;
    private readonly scrollContainer = document.getElementById("scroll-content");
    private readonly keyUpTimeoutMs: number;

    private assetList: HTMLElement;
    private retryButton: HTMLElement;
    private assetSearch: HTMLInputElement;
    private searchTimeout;
    private assetsChecklistView: ListView;
    private assetSpinner;
    private bigSpinner;
    private activeSearch: IActiveSearch | null;
    private searchToken = 0;

    constructor (options) {
        super(options, {
            title: "Select Trailers",
            pageLevel: options.pageLevel || PageLevel.TWO
        });

        this.api = this.app.api;
        this.keyUpTimeoutMs = 800;
        this.trailerCache = makeTrailerCache(this.app.store);
        this.sync = this.app.sync;
        this.user = this.app.userService.getCurrentUser();
        this.workflow = options.workflow || false;

        this.assetSpinner = new Spinner();
        this.bigSpinner = new BigSpinner();

        this.manageTrailersView = new ManageTrailersView({ app: this.app, workflow: this.workflow });

        this.detachAllView = new DetachAllPanelView({
            getConfirmMessage: () => this.getConfirmMessage(),
            getDetachErrorMessage: () => this.getDetachErrorMessage(),
            detachCallback: () => this.detachAll(),
            detachButtonText: "Detach all trailers",
            app: this.app
        });

        this.listenTo(this.manageTrailersView, "trailerRemoved", (trailerId: string) => {
            if (this.assetsChecklistView) {
                let element = trailerId && this.assetsChecklistView.collection.get(trailerId);
                element && element.set("selected", false);
            }
            this.shouldBlockAddingTrailers();
        });
        this.listenTo(this.manageTrailersView, "updateChecklist", () => {
            if (this.assetsChecklistView) {
                this.assetsChecklistView.collection.map(trailerItem => {
                    trailerItem.set("selected", this.isTrailerAttached(trailerItem.id));
                });
            }
        });
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #driver-asset-selection-retrybutton": this.retry,
            "keyup #driver-asset-selection-input": this.searchForAsset,
            "submit #select-asset-form": this.searchForAsset,
            "click #add-trailer": this.addTrailer,
            "click #trailer-continue-button": this.continueToNext
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render () {
        this.display(this.template())
            .then(() => {
                this.assetList = document.getElementById("driver-asset-selection-list") as HTMLElement;
                this.retryButton = document.getElementById("driver-asset-selection-retrybutton") as HTMLElement;
                this.assetSearch = document.getElementById("driver-asset-selection-input") as HTMLInputElement;
                this.manageTrailersView.setElement(document.getElementById("manageTrailersList")).render();

                this.shouldBlockAddingTrailers();
                this.searchForAsset();
            })
            .catch(handleApi);
        return this;
    }

    remove () {
        this.manageTrailersView?.remove();
        this.assetsChecklistView?.remove();
        this.detachAllView?.remove();
        this.searchTimeout && clearTimeout(this.searchTimeout);

        if (this.assetSpinner) {
            this.assetSpinner.stop();
            this.assetSpinner = null;
        }

        if (this.bigSpinner) {
            this.bigSpinner.stop();
            this.bigSpinner = null;
        }

        super.remove();
        return this;
    }

    searchForTrailers(searchInput: string): Promise<void> {
        let clearAssetsList = () => empty(this.assetList);
        let trailerName = cleanTrailerName(searchInput);
        this.startSearchProgress(trailerName ? this.getString("Searching for '{0}'").replace("{0}", htmlEscape(trailerName)) : this.getString("Searching for trailers..."));

        // Hide "Add trailer" before async API call
        let addTrailerDiv = document.getElementById("driver-add-new-trailer") as HTMLElement;
        addTrailerDiv && hide(addTrailerDiv);

        const assetSearchDescription = document.getElementById("asset-search-description") as HTMLElement;
        assetSearchDescription && hide(assetSearchDescription);
        let canAddTrailer = this.app.securityService.can("editTrailers");
        let shouldShowAddTrailerUI = !!trailerName && canAddTrailer;
        return this.getTrailersUsingDeviceApi(trailerName).then(result => {
                let trailers = result;
                if (trailers && trailers.length) {
                    const attachedTrailers = this.getActiveTrailerAttachments().map(attachment => attachment.getAttachmentTrailer(this.app.store) as ITrailer);
                    trailers = attachedTrailers.concat(trailers.filter(trailer => !attachedTrailers.some(attachedTrailer => attachedTrailer.id === trailer.id)));
                    this.constructAssetList(trailers);
                    this.stopSearchProgress();

                    // Let user make the trailer if there isn't a trailer with an identical name
                    let hasSameTrailer = hasTrailerWithSameName(trailerName, trailers);
                    if (!hasSameTrailer && shouldShowAddTrailerUI) {
                        show(addTrailerDiv);
                    } else {
                        hide(addTrailerDiv);
                    }
                } else {
                    clearAssetsList();
                    this.stopSearchProgress(this.getString("Could not find any trailers matching your search terms."));
                    shouldShowAddTrailerUI && show(addTrailerDiv);
                }
            })
            .catch((error) => {
                // A newer search superseded this one — let the new search update the UI.
                if (error instanceof StaleSearchError) {
                    return;
                }
                clearAssetsList();
                if (error.data.type === "NetworkError") {
                    this.stopSearchProgress(this.getString("Could not find trailers due to connectivity issues."));
                    canAddTrailer && show(addTrailerDiv);
                } else {
                    this.stopSearchProgress(this.getString("Please try again."));
                    show(this.retryButton);
                }
            });
    }

    getActiveSearch() {
        return this.activeSearch;
    }

    setActiveSearch(value: IActiveSearch) {
        this.activeSearch = value;
    }

    private getActiveTrailerAttachments() {
        return this.manageTrailersView.getCurrentTrailerAttachments();
    }

    private isTrailerAttached(trailerId: string) {
        return this.getActiveTrailerAttachments().some(attachment => attachment.trailer?.id === trailerId);
    }


    private getTrailersUsingDeviceApi(searchInput: string) {
        return new Promise<ITrailer[]>((resolve, reject) => {
            const fromDate = getServerDate().toISOString();
            const calls: TMultiCallParam[] = [
                [
                    "Get", {
                        typeName: "Device",
                        search: {
                            ...(searchInput && { name: `%${ searchInput }%` }),
                            groups: [{
                                id: "GroupTrailerId"
                            }],
                            fromDate
                        },
                        resultsLimit: MAX_SEARCH_ASSETS
                    }
                ]
            ];
            if (searchInput) {
                calls.push([
                    "Get", {
                        typeName: "Device",
                        search: {
                            name: searchInput,
                            groups: [{
                                id: "GroupTrailerId"
                            }],
                            fromDate
                        },
                        resultsLimit: 5
                    }
                ]);
            }
            const activeSearch = this.getActiveSearch();
            const isSearchForTheSameKey = activeSearch && activeSearch.key === searchInput;
            // fire a new search when activeSearch === null or isSearchForTheSameKey === false
            if (!isSearchForTheSameKey) {
                activeSearch && activeSearch.promise.cancel();
                const token = ++this.searchToken;
                const discardIfStale = (rejectInner: (e: Error) => void): boolean => {
                    if (this.searchToken !== token) {
                        const err = new StaleSearchError();
                        rejectInner(err);
                        reject(err);
                        return true;
                    }
                    return false;
                };
                this.setActiveSearch({
                    key: searchInput,
                    token,
                    promise: new Promise((resolveInner, rejectInner) => {
                        runMultiCallSearch<IDevice>(this.api, calls, searchInput).then((assets: IDevice[]) => {
                            // Discard stale responses — a newer search has already been issued.
                            if (discardIfStale(rejectInner)) { return; }
                            this.activeSearch = null;
                            resolve(assets.map(device => mapDeviceToTrailer(device)));
                            resolveInner();
                        }).catch(error => {
                            if (discardIfStale(rejectInner)) { return; }
                            this.activeSearch = null;
                            reject(error);
                            rejectInner(error);
                        });
                    })
                });
            }
        });
    }


    private startSearchProgress (workingMessage) {
        this.stopSearchProgress();
        this.retryButton && hide(this.retryButton);

        this.assetList && hide(this.assetList);

        let selectionProgress = document.getElementById("driver-asset-selection-progress");
        selectionProgress && show(selectionProgress);

        let searchTextElement = document.getElementById("driver-asset-selection-search-text");
        searchTextElement && (searchTextElement.innerHTML = workingMessage);

        let searchSpinner = document.getElementById("driver-asset-selection-search-spinner");
        if (searchSpinner) {
            show(searchSpinner);
            this.assetSpinner?.spin(searchSpinner);
        }
    }

    private stopSearchProgress (workingMessage?: string) {
        this.assetSpinner?.stop();

        let progressElement = document.getElementById("driver-asset-selection-progress");
        if (progressElement) {
            if (workingMessage) {
                let searchTextElement = document.getElementById("driver-asset-selection-search-text");
                searchTextElement && (searchTextElement.innerHTML = workingMessage);

                show(progressElement);
            } else {
                hide(progressElement);
            }
            hide(document.getElementById("driver-asset-selection-search-spinner") as HTMLElement);

            this.assetList && show(this.assetList);
        }
    }


    private addTrailerAttachment(trailerId: string) {
        let currentVehicle = getCurrentVehicle() as IIdEntity;

        if (currentVehicle?.id === "NoDeviceId") {
            return Promise.reject(new Error(TRAILER_ATTACHMENT_ERROR_MESSAGE));
        }

        let trailerAttachment = createTrailerAttachment(currentVehicle.id, trailerId);

        if (!this.workflow) {
            return this.sync.trailerAttachments.addItem(trailerAttachment);
        } else {
            // Add to temp storage instead of main trailer storage
            let tempAttachments = getAttachmentsFromState(this.app.store, this.app.userService);
            tempAttachments.push(trailerAttachment);
            setAttachmentsInState(this.app.store, tempAttachments, this.app.userService);
            return Promise.resolve();
        }
    }

    private addTrailer() {
        if (this.shouldBlockAddingTrailers()) {
            return;
        }

        let addBtn = <HTMLInputElement>document.getElementById("add-trailer");
        let searchBar = this.assetSearch;
        let toggleAddBtn = (disable: boolean) => {
            searchBar.disabled = disable;
            addBtn.disabled = disable;
            addBtn.textContent = this.getString(disable ? "Saving in progress" : "Add");
        };

        let trailerName = cleanTrailerName(this.assetSearch.value || "");

        let trailerCommentInput = document.querySelector("input#add-trailer-comment") as HTMLInputElement;
        let trailerComment = (trailerCommentInput.value || "").trim();

        const redirectCallback = (offlineTrailer: boolean, newTrailerId?: string) => {
            if (!offlineTrailer && newTrailerId && !this.workflow && this.app.securityService.can("useDvir")) {
                let dvirState = {
                    assetId: newTrailerId,
                    detach: false
                };
                this.app.router.navigate(`dvir/inspect,${ rison.encode(dvirState) }`, true);
            } else {
                this.manageTrailersView.refresh();
                if (this.scrollContainer) {
                    this.scrollContainer.scrollTop = 0;
                }

                toggleAddBtn(false);
                this.shouldBlockAddingTrailers();
                searchBar.value = "";
                trailerCommentInput.value = "";
                this.searchForAsset();
            }
        };

        if (trailerName !== "") {
            toggleAddBtn(true);

            const onTrailerCreated = (trailer: Trailer, offline: boolean) => {
                if (offline) {
                    trailer.id = idSerializer.get();
                    trailer._isTemp = true;
                    const codrivers = this.app.userService.getCoDrivers(this.user?.name);
                    if (codrivers && codrivers.length > 0) {
                        for (let codriver of codrivers) {
                            this.trailerCache.add(trailer, codriver.id);
                        }
                    }
                    logger.log(logger.Source.ENTITY, `Added an offline trailer '${ trailer.name || trailer.id }'`, this.user);
                } else {
                    logger.log(logger.Source.ENTITY, `Added and selected trailer '${ trailer.name || trailer.id }'`, this.user);
                }
                this.trailerCache.add(trailer);
                this.addTrailerAttachment(trailer.id).then(() => redirectCallback(offline, trailer.id)).catch(err => {
                    this.handleTrailerAttachmentError(err);
                });
            };
            const trailers = this.getActiveTrailerAttachments().map(attachment => attachment.getAttachmentTrailer(this.app.store) as ITrailer);
            let hasSameTrailer = hasTrailerWithSameName(trailerName, trailers);
            if (hasSameTrailer) {
                toastr.warning(
                    this.getString("A trailer named {0} is already attached").replace("{0}", trailerName),
                    undefined,
                    undefined,
                    {
                        isUserNightMode: this.app.store.getState().data.nightMode,
                        preventDuplicates: true
                    }
                );
                redirectCallback(true);
                return;
            }
            let numActiveTrailerAdd = 1;
            addNewTrailer(
                this.api,
                trailerName,
                trailerComment,
                this.user?.companyGroups ?? [],
                this.user?.timeZoneId
            ).then((trailer: ITrailer) => {
                onTrailerCreated(new Trailer(trailer), false);
            }).catch((error) => {
                //The way selenium test offline mode is setup, the errorCallback is called twice
                if (--numActiveTrailerAdd < 0) {
                    return;
                }
                if (error && error.data.type === "DuplicateException") {
                    let errorDialog = new Dialog(
                        "trailer-error-dialog",
                        "The trailer you are trying to add already exists.<br><br>Please try again, or go back to the previous screen to<br>choose the existing trailer.",
                        this.translator,
                        "Duplicate Trailer",
                        undefined,
                        { isNightMode: this.app.store.getState().data.nightMode }
                    );
                    errorDialog.open().catch(handleApi);
                } else if (error.data.type === "NetworkError") {
                    const offlineTrailer = createAndCacheOfflineTrailer(
                        trailerName,
                        trailerComment,
                        this.app.userService,
                        this.trailerCache
                    );
                    logger.log(logger.Source.ENTITY, `Added an offline trailer '${ offlineTrailer.name || offlineTrailer.id }'`, this.user);
                    this.addTrailerAttachment(offlineTrailer.id).then(() => redirectCallback(true, offlineTrailer.id)).catch(err => {
                        this.handleTrailerAttachmentError(err);
                    });
                } else {
                    handleApi(error);
                }
                toggleAddBtn(false);
            });
        }
    }

    private startSyncProgress() {
        let searchContainer = document.getElementById("select-asset-progress");
        if (searchContainer) {
            show(searchContainer);
            this.bigSpinner?.spin(searchContainer);
        }
    }

    private stopSyncProgress() {
        let searchContainer = document.getElementById("select-asset-progress");
        searchContainer && hide(searchContainer);
        this.bigSpinner?.stop();
    }

    private retry () {
        let searchInput = this.assetSearch.value || "";
        this.searchForTrailers(searchInput);
    }

    private toggleAssetPanel(isShow: boolean) {
        let assetPanel = document.getElementById("select-asset-panel");
        assetPanel && (isShow ? show(assetPanel) : hide(assetPanel));
    }

    private constructAssetList (assets) {
        let checklistAssets: any[] = [];
        let attachedAssets = this.getActiveTrailerAttachments().map(trailerAttachment => trailerAttachment.trailer);

        assets.forEach((asset: IIdEntity & INamedEntity) => {
            this.trailerCache.add(asset);

            let listAsset = new Trailer(asset);
            checklistAssets.push({
                id: listAsset.id,
                title: listAsset.licensePlate ? `${ listAsset.name } (${ listAsset.licensePlate })` : listAsset.name,
                subtitle: listAsset.comment || "",
                asset: listAsset,
                selected: attachedAssets.some(a => a.id === listAsset.id)
            });
        });

        this.assetsChecklistView && this.assetsChecklistView.remove();
        this.assetsChecklistView = generateChecklist({
            items: checklistAssets,
            isCheckable: true,
            labelledBy: "title-text",
            beforeSelectCallback: item => !this.isTrailerAttached((<Trailer>item.get("asset")).id),
            selectCallback: item => this.assetSelected(item.get("asset")),
            context: this,
            el: this.assetList,
            translator: this.translator,
            noTranslate: true
        });
    }

    private searchForAsset() {
        this.searchTimeout && clearTimeout(this.searchTimeout);

        // Hide "Add trailer" button immediately (before debounce)
        let addTrailerDiv = document.getElementById("driver-add-new-trailer") as HTMLElement;
        addTrailerDiv && hide(addTrailerDiv);

        if (this.assetSearch.value) {
            this.searchTimeout = setTimeout(() => {
                this.searchForTrailers(this.assetSearch.value || "");
            }, this.keyUpTimeoutMs);
        }
    }

    private assetSelected (asset: INamedEntity) {
        if (!this.shouldBlockAddingTrailers()) {
            this.selectTrailer(asset);
        }
    }

    private selectTrailer (asset: INamedEntity) {
        let selectTrailerProfiler = PerformanceProfilerFactory.getInstance(MeasureName.SelectTrailer);
        selectTrailerProfiler.start();
        const set = (): Promise => {
            // Checking if this asset is already selected.
            if (!this.isTrailerAttached(asset.id)) {
                !this.workflow && this.startSyncProgress();
                return this.addTrailerAttachment(asset.id);
            }
            return Promise.resolve();
        };

        set().then(() => {
            logger.log(logger.Source.ENTITY, `Selected trailer '${ (htmlEscape(asset.name) || asset.id) }'`, this.user);
            this.stopSyncProgress();

            if (!this.workflow && this.app.securityService.can("useDvir")) {
                let dvirLog = this.app.dvirLogsService.getLatestLog(this.app.dvirLogsService.getDvirLogs(), asset.id);
                let dvirState: any = {
                    detach: false
                };

                if (dvirLog) {
                    dvirState.id = dvirLog.id;
                } else {
                    dvirState.assetId = asset.id;
                }

                selectTrailerProfiler.end();
                this.app.router.navigate(`dvir/inspect,${ rison.encode(dvirState) }`, true);
            } else {
                this.manageTrailersView.refresh();
                this.shouldBlockAddingTrailers();

                if (this.scrollContainer) {
                    this.scrollContainer.scrollTop = 0;
                }
                selectTrailerProfiler.end();
            }
        }).catch(err => {
            this.handleTrailerAttachmentError(err);
        });
    }

    private handleTrailerAttachmentError(err) {
        if (err.message === TRAILER_ATTACHMENT_ERROR_MESSAGE) {
            toastr.error(
                "You must select a vehicle to attach a trailer.",
                undefined,
                undefined,
                {
                    isUserNightMode: this.app.store.getState().data.nightMode
                }
            );
            this.app.router.navigate("assets/main", true);
            handleApi(err, true);
        } else {
            handleApi(err);
        }
    }

    private getConfirmMessage() {
        return this.getString("Are you sure you want to detach {0} trailers?").replace("{0}", this.getActiveTrailerAttachments().length.toString());
    }

    private getDetachErrorMessage() {
        return this.getString("Cannot attach more than {0} trailers. Please detach existing trailers in order to add new assets.")
            .replace("{0}", constants.ASSET_LIMITS.MAX_TRAILERS_LIMIT.toString());
    }

    private detachAll() {
        let attachments = this.getActiveTrailerAttachments();
        return detachAllTrailers(attachments, this.sync)
            .then(() => {
                setAttachmentsInState(this.app.store, [], this.app.userService);
                this.manageTrailersView.refresh(false);
                !this.workflow && this.sync.synchronize(false, null, [SyncType.TrailerAttachments]);
                this.render();
            });
    }

    private shouldBlockAddingTrailers() {
        let assetDetachTrailers = document.querySelector("#select-asset-detach-trailers") as HTMLElement;
        let activeTrailerCount = this.getActiveTrailerAttachments().length;
        if (activeTrailerCount >= constants.ASSET_LIMITS.MAX_TRAILERS_LIMIT) {
            if (assetDetachTrailers && this.detachAllView) {
                this.detachAllView.setElement(assetDetachTrailers);
                this.detachAllView.show(activeTrailerCount > constants.ASSET_LIMITS.MAX_TRAILERS_LIMIT);
                show(assetDetachTrailers);
            }
            this.toggleAssetPanel(false);
            return true;
        } else {
            assetDetachTrailers && hide(assetDetachTrailers);
            this.toggleAssetPanel(true);
            return false;
        }
    }

    private continueToNext () {
        if (this.workflow) {
            dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep);
        } else {
            this.app.router.navigate("assets/main", true);
        }
    }
}